import React, { useEffect, useMemo, useRef, useState } from 'react';
import { themes } from '../config/themes';
import { formatEventHostsLabel, getEventGridDayKeys, isEventCanceled, isMultiDayEvent, getLocalDateKey, parseEventDate } from '../utils/eventUtils';
import SkylineWatermark from './calendar/SkylineWatermark';
import CalendarLegend from './calendar/CalendarLegend';
import WeekEventPreview from './calendar/WeekEventPreview';
import MobileSheet, { MOBILE_SHEET_ANIMATION_MS } from './calendar/MobileSheet';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import { CATEGORIES, catColor, categorizeEvent } from '../config/categories';
import { fs, sz } from '../utils/typography';

const DOW_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const START_HOUR = 7;
const END_HOUR = 22;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const GUTTER_W = 52;
// Mobile: share of the row the tapped (focused) day takes vs. each other day.
const FOCUSED_DAY_FR = 6;
// Landscape phones (`compact`): the hour grid scrolls at this height per hour
// instead of squeezing all TOTAL_HOURS into ~250px (under 20px an hour).
const COMPACT_HOUR_PX = 34;
const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => START_HOUR + i);

const fmtHour = (h) => {
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

const fmtTime = (date) => date
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : '';

const getEventTimeRange = (event) => {
    const start = parseEventDate(event.startDate);
    if (!start) return null;
    const end = parseEventDate(event.endDate || event.startDate);
    const startH = start.getHours() + start.getMinutes() / 60;
    const endH = end && end.getTime() !== start.getTime()
        ? end.getHours() + end.getMinutes() / 60
        : startH + 1;
    return { start, end, startH, endH };
};

const fmtSpan = (event) => {
    const opts = { month: 'short', day: 'numeric' };
    const start = parseEventDate(event.startDate);
    const end = parseEventDate(event.endDate);
    return start && end ? `${start.toLocaleDateString([], opts)} – ${end.toLocaleDateString([], opts)}` : '';
};

const isAllDayEvent = (event) => {
    const range = getEventTimeRange(event);
    return !range || (range.start.getHours() === 0 && range.start.getMinutes() === 0);
};

const isOutsideVisibleHours = (event) => {
    const range = getEventTimeRange(event);
    return range && (range.endH <= START_HOUR || range.startH >= END_HOUR);
};

// Returns top/height as percentages for a timed event within the hour grid.
const getBlock = (event) => {
    const range = getEventTimeRange(event);
    if (!range || range.startH === 0) return null; // treat midnight as all-day
    const { startH, endH } = range;
    const clampedStart = Math.max(startH, START_HOUR);
    const clampedEnd = Math.min(Math.max(endH, startH + 0.5), END_HOUR);
    if (clampedEnd <= clampedStart) return null;
    return {
        top: ((clampedStart - START_HOUR) / TOTAL_HOURS) * 100,
        height: ((clampedEnd - clampedStart) / TOTAL_HOURS) * 100,
        startH,
        endH,
    };
};

// Assigns non-overlapping column slots to events within one day.
const resolveColumns = (dayEvents) => {
    const items = dayEvents
        .map((event) => ({ event, block: getBlock(event) }))
        .filter((x) => x.block);

    items.sort((a, b) => a.block.startH - b.block.startH);

    const clusters = [];
    items.forEach((item) => {
        const last = clusters[clusters.length - 1];
        if (!last || item.block.startH >= last.endH - 0.05) {
            clusters.push({ items: [item], endH: item.block.endH });
            return;
        }
        last.items.push(item);
        last.endH = Math.max(last.endH, item.block.endH);
    });

    const positioned = clusters.flatMap((cluster) => {
        const slots = []; // slots[i] = endH of last event in column i
        const result = cluster.items.map(({ event, block }) => {
            let col = 0;
            while (slots[col] !== undefined && slots[col] > block.startH + 0.05) col++;
            slots[col] = block.endH;
            return { event, block, col };
        });
        const totalCols = Math.max(1, ...result.map((r) => r.col + 1));
        return result.map((item) => ({ ...item, totalCols }));
    });

    return { positioned };
};

// Week view — "Atlanta Editorial" design refresh: charcoal day header, scrollable
// hour-grid with time gutter and duration-positioned event blocks, all-day row,
// skyline watermark, legend footer.
//
// On mobile (`isMobile`), seven columns are too narrow to read, so tapping an
// event doesn't jump to the day pane. It widens that day's column and opens a
// WeekEventPreview in a bottom sheet (the same MobileSheet the details use),
// whose "Details" opens the full details. While the sheet is open the hour
// grid shrinks to fit above it, so no event is hidden underneath. Tapping
// empty space in a column focuses that day (and closes any preview); tapping
// a day header still opens the day pane.
//
// `compact` is a phone held sideways (see useCompactLandscape): columns are
// wide enough (~110px) to read, but height is short, so the hour grid scrolls
// at COMPACT_HOUR_PX per hour, opening at the current time (this week) or the
// week's first event, and day headers sit on one line with a smaller number
// ("MON 28"; just "M 28" while `panelOpen` — the day panel — narrows the columns).
const WeekView2 = ({ days, events, theme, onDaySelect, onEventSelect, selectedDay, legendEvents, selectedCategory, selectedTags, onCategoryClick, onTagClick, isMobile = false, compact = false, panelOpen = false }) => {
    const tokens = theme?.tokens || themes.light.tokens;
    const c = tokens.colors;
    const [hoveredEventId, setHoveredEventId] = useState(null);
    const prefersReducedMotion = usePrefersReducedMotion();
    const [focusedKey, setFocusedKey] = useState(null); // mobile: widened day column
    // mobile preview: `peek` ({ event, day }) outlives `isPeekOpen` by the
    // sheet's close animation so its content doesn't go blank mid-slide.
    const [peek, setPeek] = useState(null);
    const [isPeekOpen, setIsPeekOpen] = useState(false);
    const [sheetHeight, setSheetHeight] = useState(0);
    const peekCloseTimeoutRef = useRef(null);

    const openPeek = (event, day) => {
        if (peekCloseTimeoutRef.current) clearTimeout(peekCloseTimeoutRef.current);
        peekCloseTimeoutRef.current = null;
        setPeek({ event, day });
        setIsPeekOpen(true);
    };

    const closePeek = (immediate = false) => {
        setIsPeekOpen(false);
        if (peekCloseTimeoutRef.current) clearTimeout(peekCloseTimeoutRef.current);
        if (immediate || prefersReducedMotion) {
            setPeek(null);
            peekCloseTimeoutRef.current = null;
            return;
        }
        peekCloseTimeoutRef.current = window.setTimeout(() => {
            setPeek(null);
            peekCloseTimeoutRef.current = null;
        }, MOBILE_SHEET_ANIMATION_MS);
    };

    useEffect(() => () => {
        if (peekCloseTimeoutRef.current) clearTimeout(peekCloseTimeoutRef.current);
    }, []);

    // A new week (or leaving mobile) starts with equal columns and no preview.
    const weekKey = days.length ? getLocalDateKey(days[0]) : '';
    useEffect(() => {
        setFocusedKey(null);
        closePeek(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekKey, isMobile]);

    // Landscape: scroll the hour grid to where the week's events are.
    const hourScrollRef = useRef(null);
    useEffect(() => {
        const el = hourScrollRef.current;
        if (!compact || !el) return;
        const nowD = new Date();
        const weekKeys = days.map((d) => getLocalDateKey(d));
        let targetH;
        if (weekKeys.includes(getLocalDateKey(nowD))) {
            targetH = nowD.getHours() + nowD.getMinutes() / 60 - 1;
        } else {
            const starts = (events || [])
                .filter((e) => !isAllDayEvent(e) && !isOutsideVisibleHours(e) && getEventGridDayKeys(e).length === 1)
                .map((e) => getEventTimeRange(e).startH);
            targetH = starts.length ? Math.min(...starts) - 0.5 : 8;
        }
        const frac = (Math.max(START_HOUR, Math.min(targetH, END_HOUR)) - START_HOUR) / TOTAL_HOURS;
        el.scrollTop = frac * el.scrollHeight;
        // Only when the week (or layout) changes — not on every event refresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compact, weekKey]);

    const focusActive = isMobile && !!focusedKey;
    const gridColumns = focusActive
        ? `${GUTTER_W}px ${days.map((d) => (getLocalDateKey(d) === focusedKey ? `minmax(0, ${FOCUSED_DAY_FR}fr)` : 'minmax(0, 1fr)')).join(' ')}`
        : `${GUTTER_W}px repeat(7, minmax(0, 1fr))`;
    const columnTransition = prefersReducedMotion ? undefined : 'grid-template-columns 220ms ease';

    const { allDayByDay, timedByDay, outOfRangeByDay } = useMemo(() => {
        const allDayByDay = {};
        const timedByDay = {};
        const outOfRangeByDay = {};
        (events || []).forEach((event) => {
            const keys = getEventGridDayKeys(event);
            if (keys.length === 0) return;
            // Multi-day events go in the all-day row on each day they cover:
            // their start/end times describe the whole span, not a daily slot.
            if (keys.length > 1 || isAllDayEvent(event)) {
                keys.forEach((key) => { (allDayByDay[key] = allDayByDay[key] || []).push(event); });
            } else if (isOutsideVisibleHours(event)) {
                (outOfRangeByDay[keys[0]] = outOfRangeByDay[keys[0]] || []).push(event);
            } else {
                (timedByDay[keys[0]] = timedByDay[keys[0]] || []).push(event);
            }
        });
        return { allDayByDay, timedByDay, outOfRangeByDay };
    }, [events]);

    const todayKey = getLocalDateKey(new Date());
    const selectedKey = selectedDay ? getLocalDateKey(selectedDay) : null;

    // Current time indicator
    const now = new Date();
    const nowFrac = now.getHours() + now.getMinutes() / 60;
    const nowTop = ((nowFrac - START_HOUR) / TOTAL_HOURS) * 100;
    const showNow = nowFrac >= START_HOUR && nowFrac < END_HOUR;
    const todayInWeek = days.some((d) => getLocalDateKey(d) === todayKey);

    const openDetails = (event, day, e) => {
        if (onDaySelect) onDaySelect(day);
        if (onEventSelect) onEventSelect(event, e, day);
    };

    const handleEventClick = (event, day, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (isMobile) {
            // A second tap on the previewed (expanded) block closes the preview,
            // uncovering any overlapping neighbors.
            if (isPeeked(event, day)) {
                closePeek();
                return;
            }
            setFocusedKey(getLocalDateKey(day));
            openPeek(event, day);
            return;
        }
        openDetails(event, day, e);
    };

    // Mobile: a tap on empty column space focuses that day and closes any preview.
    const handleColumnTap = (day) => {
        if (isPeekOpen) closePeek();
        setFocusedKey(getLocalDateKey(day));
    };

    const isPeeked = (event, day) => isPeekOpen && peek && peek.event.id === event.id && getLocalDateKey(peek.day) === getLocalDateKey(day);

    return (
        <div
            className="w-full h-full flex flex-col overflow-hidden"
            style={{ background: c.surface, color: c.ink, fontFamily: tokens.fonts.ui, textAlign: 'left' }}
            role="region"
            aria-label="Week calendar"
        >
            {/* main content — skyline anchored here (same scope as month grid) */}
            <div className="flex-1 min-h-0 flex flex-col relative overflow-hidden">
            <SkylineWatermark tokens={tokens} />
            {/* charcoal day header band */}
            <div
                className="grid flex-none"
                style={{
                    gridTemplateColumns: gridColumns, transition: columnTransition,
                    background: c.headBg, borderBottom: `1px solid ${c.gridLine}`,
                    position: 'relative', zIndex: 2,
                }}
                role="row"
            >
                <div /> {/* gutter spacer */}
                {days.map((day) => {
                    const dateKey = getLocalDateKey(day);
                    const isSelectedDay = dateKey === selectedKey;
                    const isTodayDay = dateKey === todayKey;
                    const squeezed = focusActive && dateKey !== focusedKey;
                    // Selected day: inverted light pill (12:1 on the charcoal band).
                    // Today: the accent pill Month view uses. Accent-colored text
                    // alone was only 2.6:1 on the band in light mode.
                    const numberPill = isSelectedDay
                        ? { background: c.headInk, color: c.headBg }
                        : isTodayDay ? { background: c.today, color: c.todayInk } : null;
                    return (
                        <div
                            key={dateKey}
                            className={compact
                                // One line ("MON 28") with a smaller number, to save height.
                                ? 'flex flex-row items-center justify-center gap-1 py-1.5 px-0.5 cursor-pointer'
                                : 'flex flex-col lg:flex-row items-center lg:items-baseline justify-center lg:justify-start gap-1 lg:gap-2 py-2 lg:py-3 px-1 lg:px-3 cursor-pointer'}
                            onClick={() => { closePeek(true); onDaySelect(day); }}
                            style={{ borderLeft: '1px solid rgba(255,255,255,0.08)', minWidth: 0 }}
                            role="columnheader"
                            aria-selected={isSelectedDay}
                            aria-current={isTodayDay ? 'date' : undefined}
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDaySelect(day); } }}
                            aria-label={day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        >
                            <span style={{ fontFamily: tokens.fonts.ui, fontSize: fs(11), fontWeight: isSelectedDay ? 800 : 600, letterSpacing: 0.8, textTransform: 'uppercase', color: c.headInk }}>
                                {(squeezed || (compact && panelOpen)) ? DOW_SHORT[day.getDay()][0] : DOW_SHORT[day.getDay()]}
                            </span>
                            <span style={{
                                fontFamily: tokens.fonts.display, fontSize: (squeezed || compact) ? fs(15) : fs(22), fontWeight: 500, lineHeight: 1, color: c.headInk,
                                ...(numberPill ? {
                                    ...numberPill, borderRadius: sz(14), minWidth: (squeezed || compact) ? sz(22) : sz(30),
                                    padding: (squeezed || compact) ? '2px 4px' : '3px 6px', display: 'inline-block', textAlign: 'center',
                                } : {}),
                            }}>
                                {day.getDate()}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* all-day row */}
            <div
                className="grid flex-none"
                style={{
                    gridTemplateColumns: gridColumns, transition: columnTransition,
                    borderBottom: `1px solid ${c.gridLine}`,
                    background: c.surfaceAlt,
                    // Two chips fit per day; more scroll within the cell. Landscape
                    // phones (`compact`) get a single-chip row plus a "+N" that opens the day.
                    height: compact ? sz(24) : sz(46),
                }}
            >
                <div style={{
                    fontFamily: tokens.fonts.mono, fontSize: fs(8.5), letterSpacing: 0.5, textTransform: 'uppercase',
                    color: c.faint, padding: compact ? '0 6px' : '5px 6px 0', textAlign: 'right', alignSelf: 'stretch', lineHeight: compact ? sz(24) : 1.15,
                    whiteSpace: compact ? 'nowrap' : undefined, borderRight: `1px solid ${c.gridLine}`,
                }}>
                    All day
                </div>
                {days.map((day) => {
                    const dateKey = getLocalDateKey(day);
                    const allDayEvents = allDayByDay[dateKey] || [];
                    const outOfRangeEvents = outOfRangeByDay[dateKey] || [];
                    const cellEvents = [...allDayEvents, ...outOfRangeEvents];
                    const shownEvents = compact ? cellEvents.slice(0, 1) : cellEvents;
                    const hiddenCount = cellEvents.length - shownEvents.length;
                    return (
                        <div
                            key={dateKey}
                            onClick={isMobile ? () => handleColumnTap(day) : undefined}
                            style={{
                                borderLeft: `1px solid ${c.gridLine}`, padding: focusActive && dateKey !== focusedKey ? '3px 2px' : '3px 4px',
                                display: 'flex', flexDirection: compact ? 'row' : 'column', alignItems: compact ? 'center' : undefined,
                                gap: compact ? 3 : 2, overflowY: compact ? 'hidden' : 'auto', minWidth: 0,
                            }}
                        >
                            {shownEvents.map((event) => {
                                const cat = categorizeEvent(event);
                                const cc = catColor(cat, tokens);
                                const outOfRange = outOfRangeEvents.includes(event);
                                const canceled = isEventCanceled(event);
                                return (
                                    <div
                                        key={event.id}
                                        onClick={(e) => handleEventClick(event, day, e)}
                                        style={{
                                            borderLeft: `3px solid ${cc.strong}`,
                                            borderTop: outOfRange ? `1px dashed ${cc.line}` : `1px solid ${cc.line}`,
                                            borderRight: outOfRange ? `1px dashed ${cc.line}` : `1px solid ${cc.line}`,
                                            borderBottom: outOfRange ? `1px dashed ${cc.line}` : `1px solid ${cc.line}`,
                                            background: cc.fillBg,
                                            borderRadius: 4, padding: '1px 5px', cursor: 'pointer', flex: compact ? '1 1 auto' : '0 0 auto', minWidth: 0,
                                            fontFamily: tokens.fonts.ui, fontSize: fs(11.5), fontWeight: 600,
                                            color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            lineHeight: 1.2, opacity: canceled ? 0.55 : 1,
                                            textDecoration: canceled ? 'line-through' : 'none',
                                            boxShadow: isPeeked(event, day) ? `0 0 0 2px ${cc.strong}` : 'none',
                                        }}
                                        title={canceled ? `${event.title} — Canceled` : outOfRange ? `${event.title} — outside visible hours` : isMultiDayEvent(event) ? `${event.title} — ${fmtSpan(event)}` : event.title}
                                    >
                                        {outOfRange && (
                                            <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(10), color: cc.strong, marginRight: 4 }}>
                                                {fmtTime(parseEventDate(event.startDate))}
                                            </span>
                                        )}
                                        <i className={`fas ${CATEGORIES[cat].icon}`} style={{ color: cc.strong, fontSize: fs(9), marginRight: 3 }} aria-hidden="true" />
                                        {canceled && (
                                            <i className="fas fa-ban" style={{ color: c.faint, fontSize: fs(10), marginRight: 3 }} aria-label="Canceled event" />
                                        )}
                                        {!canceled && event.eventType === 'Featured' && (
                                            <i className="fas fa-star" style={{ color: cc.strong, fontSize: fs(10), marginRight: 3 }} aria-label="Featured event" />
                                        )}
                                        {event.title}
                                    </div>
                                );
                            })}
                            {hiddenCount > 0 && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onDaySelect(day); }}
                                    style={{
                                        flex: '0 0 auto', fontFamily: tokens.fonts.mono, fontSize: fs(10), fontWeight: 700, color: c.accent,
                                        background: 'transparent', border: 'none', padding: '0 2px', cursor: 'pointer',
                                    }}
                                    aria-label={`Show ${hiddenCount} more all-day event${hiddenCount > 1 ? 's' : ''} for ${day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
                                >
                                    +{hiddenCount}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* scaled hour grid (scrolling at a fixed hour height in landscape) */}
            <div
                ref={hourScrollRef}
                className={`flex-1 min-h-0 ${compact ? 'overflow-y-auto' : 'overflow-hidden'}`}
                // Top padding keeps the first hour label (centered on its line) from being clipped.
                style={{ position: 'relative', overscrollBehavior: 'contain', paddingTop: compact ? 8 : 0 }}
            >
                <div
                    className="grid"
                    style={{
                        gridTemplateColumns: gridColumns, transition: columnTransition,
                        height: compact ? sz(COMPACT_HOUR_PX * TOTAL_HOURS) : '100%', position: 'relative', zIndex: 1,
                    }}
                    role="grid"
                >
                    {/* time gutter */}
                    <div style={{
                        position: 'relative', height: '100%', borderRight: `1px solid ${c.gridLine}`,
                        background: `color-mix(in srgb, ${c.surface} 50%, transparent)`,
                    }}>
                        {hours.map((h) => (
                            <div
                                key={h}
                                style={{
                                    position: 'absolute', top: `${((h - START_HOUR) / TOTAL_HOURS) * 100}%`,
                                    right: 8, transform: 'translateY(-50%)',
                                    fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.3,
                                    color: c.faint, fontWeight: 500, whiteSpace: 'nowrap',
                                }}
                                aria-hidden="true"
                            >
                                {fmtHour(h)}
                            </div>
                        ))}
                    </div>

                    {/* day columns */}
                    {days.map((day, i) => {
                        const dateKey = getLocalDateKey(day);
                        const dayEvents = timedByDay[dateKey] || [];
                        const isSelected = dateKey === selectedKey;
                        const isToday = dateKey === todayKey;
                        const { positioned } = resolveColumns(dayEvents);

                        return (
                            <div
                                key={dateKey}
                                style={{
                                    borderLeft: `1px solid ${c.gridLine}`,
                                    background: isSelected
                                        ? `color-mix(in srgb, ${c.selBg} 50%, transparent)`
                                        : `color-mix(in srgb, ${c.surface} 50%, transparent)`,
                                    boxShadow: isSelected ? `inset 0 0 0 2px ${c.selRing}` : 'none',
                                    position: 'relative', height: '100%', cursor: 'pointer',
                                }}
                                onClick={() => (isMobile ? handleColumnTap(day) : onDaySelect(day))}
                                role="gridcell"
                                aria-label={day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                            >
                                {/* hour lines */}
                                {hours.map((h) => (
                                    <div
                                        key={h}
                                        style={{
                                            position: 'absolute', top: `${((h - START_HOUR) / TOTAL_HOURS) * 100}%`,
                                            left: 0, right: 0,
                                            borderTop: `1px solid ${c.gridLine}`,
                                            opacity: 0.6,
                                        }}
                                        aria-hidden="true"
                                    />
                                ))}

                                {/* current time indicator */}
                                {showNow && isToday && todayInWeek && (
                                    <div
                                        style={{ position: 'absolute', top: `${nowTop}%`, left: 0, right: 0, zIndex: 4, pointerEvents: 'none' }}
                                        aria-hidden="true"
                                    >
                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                            <div style={{ width: 8, height: 8, borderRadius: 4, background: c.accent, marginLeft: -4, flex: '0 0 auto' }} />
                                            <div style={{ flex: 1, height: 1.5, background: c.accent }} />
                                        </div>
                                    </div>
                                )}

                                {/* timed events */}
                                {positioned.map(({ event, block, col, totalCols }) => {
                                    const cat = categorizeEvent(event);
                                    const cc = catColor(cat, tokens);
                                    const dark = tokens.mode === 'dark';
                                    const colW = 100 / totalCols;
                                    const colLeft = col * colW;
                                    // Touch taps fire emulated mouse events; on mobile the widened
                                    // day column replaces hover-to-expand, which would cover neighbors.
                                    const isHovered = !isMobile && hoveredEventId === event.id;
                                    // Expand to the full column width on desktop hover, and on
                                    // mobile while this block's preview is open (it may cover
                                    // overlapping neighbors until the preview closes).
                                    const isExpanded = isHovered || isPeeked(event, day);
                                    const isShort = block.height < 48;
                                    const canceled = isEventCanceled(event);
                                    return (
                                        <div
                                            key={event.id}
                                            onClick={(e) => handleEventClick(event, day, e)}
                                            onMouseOver={() => setHoveredEventId(event.id)}
                                            onMouseEnter={() => setHoveredEventId(event.id)}
                                            onMouseMove={() => setHoveredEventId(event.id)}
                                            onMouseLeave={() => setHoveredEventId(null)}
                                            onFocus={() => setHoveredEventId(event.id)}
                                            onBlur={() => setHoveredEventId(null)}
                                            style={{
                                                position: 'absolute',
                                                top: `calc(${block.top}% + 1px)`,
                                                height: `calc(${block.height}% - 2px)`,
                                                minHeight: sz(28),
                                                left: isExpanded ? 3 : `calc(${colLeft}% + 3px)`,
                                                width: isExpanded ? 'calc(100% - 6px)' : `calc(${colW}% - 6px)`,
                                                background: `color-mix(in srgb, ${dark ? c.surfaceAlt : c.surface} 94%, transparent)`,
                                                borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                                                borderLeft: `3px solid ${cc.strong}`,
                                                padding: isShort ? '3px 7px' : '6px 8px',
                                                zIndex: isExpanded ? 8 : 3,
                                                opacity: canceled ? 0.55 : 1,
                                                boxShadow: isPeeked(event, day)
                                                    ? `0 0 0 2px ${cc.strong}, 0 5px 18px ${dark ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.18)'}`
                                                    : isHovered
                                                        ? `0 5px 18px ${dark ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.18)'}`
                                                        : `0 1px 5px rgba(0,0,0,0.09)`,
                                                transition: 'left 150ms ease, width 150ms ease, box-shadow 150ms ease',
                                            }}
                                            title={canceled ? `${event.title} — Canceled` : `${event.title} — ${fmtTime(parseEventDate(event.startDate))}`}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEventClick(event, day, e); } }}
                                            aria-label={`${event.title} at ${fmtTime(parseEventDate(event.startDate))}${canceled ? ' (canceled)' : ''}`}
                                        >
                                            <div style={{
                                                fontFamily: tokens.fonts.ui, fontSize: isShort ? fs(13.5) : fs(15), fontWeight: 700, color: c.ink,
                                                lineHeight: isShort ? 1.12 : 1.25, overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: isShort ? 'nowrap' : 'normal',
                                                display: isShort ? 'block' : '-webkit-box',
                                                WebkitLineClamp: isShort ? 'unset' : 2, WebkitBoxOrient: 'vertical',
                                                textDecoration: canceled ? 'line-through' : 'none',
                                            }}>
                                                <i className={`fas ${CATEGORIES[cat].icon}`} style={{ color: cc.strong, fontSize: isShort ? fs(10) : fs(11), marginRight: 3 }} aria-hidden="true" />
                                                {canceled && (
                                                    <i className="fas fa-ban" style={{ color: c.faint, fontSize: fs(12), marginRight: 4 }} aria-label="Canceled event" />
                                                )}
                                                {!canceled && event.eventType === 'Featured' && (
                                                    <i className="fas fa-star" style={{ color: cc.strong, fontSize: fs(12), marginRight: 4 }} />
                                                )}
                                                {event.title}
                                            </div>
                                            <div style={{
                                                fontFamily: tokens.fonts.ui, fontSize: isShort ? fs(12) : fs(13.5), fontWeight: 600,
                                                color: c.muted, lineHeight: isShort ? 1.08 : 1.18,
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                marginTop: isShort ? 1 : 3,
                                            }}>
                                                {formatEventHostsLabel(event)}
                                            </div>
                                            <div style={{
                                                fontFamily: tokens.fonts.mono, fontSize: isShort ? fs(9.5) : fs(10.5), fontWeight: 600,
                                                letterSpacing: 0.2, color: cc.strong, whiteSpace: 'nowrap',
                                                overflow: 'hidden', textOverflow: 'ellipsis',
                                                marginTop: isShort ? 1 : 3,
                                            }}>
                                                {fmtTime(parseEventDate(event.startDate))}
                                            </div>
                                            {event.sponsorLogo && (
                                                <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <img
                                                        src={event.sponsorLogo}
                                                        alt={event.sponsorName || 'Sponsor'}
                                                        style={{ maxHeight: 16, maxWidth: '80%', objectFit: 'contain', opacity: 0.85 }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Mobile: reserves the open preview sheet's height so the hour grid
                shrinks to fit above it instead of being covered. */}
            {isMobile && (
                <div
                    aria-hidden="true"
                    style={{
                        flex: '0 0 auto', height: sheetHeight,
                        transition: prefersReducedMotion ? 'none' : `height ${MOBILE_SHEET_ANIMATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
                    }}
                />
            )}
            {isMobile && (
                <MobileSheet
                    isOpen={isPeekOpen}
                    onDismiss={() => closePeek()}
                    tokens={tokens}
                    ariaLabel="Close preview (drag down or press)"
                    onHeightChange={setSheetHeight}
                >
                    {peek && (
                        <WeekEventPreview
                            event={peek.event}
                            day={peek.day}
                            tokens={tokens}
                            onOpenDetails={() => { const { event, day } = peek; closePeek(true); openDetails(event, day); }}
                            onClose={() => closePeek()}
                        />
                    )}
                </MobileSheet>
            )}
            </div>

            <CalendarLegend
                tokens={tokens}
                events={legendEvents}
                selectedCategory={selectedCategory}
                selectedTags={selectedTags}
                onCategoryClick={onCategoryClick}
                onTagClick={onTagClick}
            />
        </div>
    );
};

export default WeekView2;
